import {
    Button,
    Card,
    CardActions,
    CardHeader,
    colors,
    Grid,
    InputAdornment, MenuItem,
    Modal, Select,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useRef, useState} from 'react';
import CardContent from '@material-ui/core/CardContent';
import getNexioToken from '../PaymentCheckoutFrame/getNexioToken.api';
import {T} from 'react-polyglot-hooks';
import {PaymentStatus, PaymentType, PaymentTypes} from '../../../graphql/payments.datatypes';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import getBillingParty from '../../BillingParty/getBillingParty.api';
import anyLoading from '../../../utils/anyLoading';
import createAutomaticPayment, {CreateAutomaticPaymentInput} from './createAutomaticPayment.api'
import createPayment, {CreatePaymentInput} from '../PaymentEditModal/createPayment.api';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import {formatMoney} from '../../../utils/formatters';
import InfoPopover from '../../../components/InfoPopover';
import {BillingParty} from '../../../graphql/billing_party.datatypes';

const image = require('../assets/echecke.png');

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 500,
        height: 575,
        maxHeight: '100%',
        overflowY: 'auto',
        maxWidth: '100%'
    },
    summaryTable: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    iframe: {
        border: 0,
        width: '100%',
        height: 400,
        marginTop: theme.spacing(1),
    },
    container: {
        marginTop: theme.spacing(0),
        paddingTop: 0,
        paddingBottom: 0,
    },
    actions: {
        justifyContent: 'flex-end'
    },
    deleteButton: {
        color: theme.palette.white,
        backgroundColor: colors.red[600],
        '&:hover': {
            backgroundColor: colors.red[900]
        }
    },
    submitButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    image: {
        marginTop: 50,
        display: 'inline-block',
        maxWidth: '100%',
        width: 560
    }
}));

const AutomaticPaymentCheckoutFrame = (props: {
    onClose: () => void,
    onSuccess: () => void,
    open: boolean,
    billingParty?: BillingParty,
    billingPartyId?: number,
    billingPartyMemberId?: number,
    amount?: number,
    message?: string,
}) => {
    const {open, onClose, onSuccess, billingPartyId, message } = props;
    const [paymentFrequency, setPaymentFrequency] = useState(-1);

    const classes = useStyles();
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [frameUrl, setFrameUrl] = useState('');
    const [isNexioValid, setisNexioValid] = useState(false);
    const [paymentType, setPaymentType] = useState<number>(-1);
    const [amount, setAmount] = useState(props.amount ? props.amount : 0);
    const [nexioAmount, setNexioAmount] = useState(0);
    const [creditCardFee, setCreditCardFee] = useState(0);
    const [error, setError] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [sendAutomaticCreatePayment] = createAutomaticPayment();
    const {enqueueSnackbar} = useSnackbar();
    const [processing, setProcessing] = useState(false);

    const billingPartyData = getBillingParty(billingPartyId ? billingPartyId : -1);
    const [billingPartyMemberId, setBillingPartyMemberId] = useState(props.billingPartyMemberId ? props.billingPartyMemberId : -1);
    const billingParty = props.billingParty ? props.billingParty : billingPartyData.data && billingPartyData.data.billingParty;

    const tokenData = getNexioToken(nexioAmount, paymentType, billingPartyMemberId);
    const nexioToken = tokenData.data && tokenData.data.getNexioToken;

    const loadingData = anyLoading(tokenData, billingPartyData);

    const validateCardForm = (data: any) => {
        return data.billToAddressOneValid &&
            data.billToAddressTwoValid &&
            data.billToCityValid &&
            data.billToCountryValid &&
            data.billToPostalValid &&
            data.billToStateValid &&
            data.cardExpirationValid &&
            data.cardHolderNameValid &&
            data.cardNumberValid &&
            data.cardSecurityCodeValid &&
            data.cardTypeValid;
    };

    const validateEchequeForm = (data: any) => {
        return data.accountHolderNameValid &&
            //data.accountNumberValid &&
            data.billToAddressOneValid &&
            data.billToAddressTwoValid &&
            data.billToCityValid &&
            data.billToCountryValid &&
            data.billToPostalValid &&
            data.billToStateValid &&
            data.routingNumberValid;
    };

    const savePaymentData = (data: any) => {
        setProcessing(true);

        const sanitisedData: CreateAutomaticPaymentInput = {
            data: {
                payment_token: data.token.token,
                payment_type: paymentType, // will be a 0 or 1 for card or echeck
                billing_party_member_id: billingPartyMemberId,
                payment_frequency: paymentFrequency,
                last_four: data.token.lastFour,
                routing_number: data && data.bank && data.bank.routingNumber
            }
        };
        // send sanitized data to query
        sendAutomaticCreatePayment({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Automatic Payment Saved.', {variant: 'success'});
                onSuccess();
            })
            .catch(() => {
                enqueueSnackbar('Save Automatic Payment Failed!', {variant: 'error'});
                onClose();
            });
    };

    // this is used to determine when we get responses from nexio and read its status
    useEffect(() => {
        const messageListener = (event: any) => {
            if (nexioToken) {
                if (event.origin === nexioToken.baseUrl) {
                    switch (event.data.event) {
                        case 'loaded':
                            setLoading(false);
                            break;
                        case 'formValidations':
                            if (paymentType === PaymentType.CARD) setisNexioValid(validateCardForm(event.data.data));
                            if (paymentType === PaymentType.ECHEQUE) setisNexioValid(validateEchequeForm(event.data.data));
                            break;
                        case 'submit':
                            setLoading(true);
                            break;
                        case 'eCheckSaved':
                        case 'cardSaved':
                            savePaymentData(event.data.data);
                            break;
                        case 'error':
                            setLoading(false);
                            setErrorFromNexio(event.data.data.message);
                            break;
                    }
                }
            }
        };
        window.addEventListener('message', messageListener);
        return () => {
            window.removeEventListener('message', messageListener);
        };
    });

    const setErrorFromNexio = (errorMessage: string) => {
        let newMessage = 'Payment Error: Please contact us at 1-800-222-4432 for more information.';
        if (errorMessage.indexOf('Credit card has expired') === 0) {
            newMessage = 'Payment Error: Credit card has expired.';
        }
        if (errorMessage.indexOf('Verify CVC Failed') === 0) {
            newMessage = 'Payment Error: CVC Failed.';
        }
        if (errorMessage.indexOf('Card number error') === 0) {
            newMessage = 'Payment Error: Card number error.';
        }
        if (errorMessage.indexOf('VCERR') === 0) {
            newMessage = 'Payment Error: Routing number invalid.';
        }
        setError(newMessage);
    };

    useEffect(() => {
        setError(undefined);

        if (paymentType !== -1 && nexioToken && nexioToken.baseUrl) {
            setLoading(true);
            let nexioUrl = nexioToken.baseUrl;
            if (paymentType === PaymentType.CARD) nexioUrl += '/pay/v3/saveCard';
            if (paymentType === PaymentType.ECHEQUE) nexioUrl += '/pay/v3/saveECheck';
            nexioUrl += '?token=' + nexioToken.token + '&shouldReturnHtml=false';
            setFrameUrl(nexioUrl);
        }

        if (nexioToken && nexioToken.error) {
            setError(nexioToken.error);
        }
    }, [nexioToken, paymentType]);

    // submission to Nexio
    const onPaymentSubmit = () => {
        if (frameRef && frameRef.current && frameRef.current.contentWindow) {
            if (nexioToken && nexioToken.baseUrl) {
                // response will have token info
                const response = frameRef.current.contentWindow.postMessage('posted', nexioToken.baseUrl);
            }
        }
    };

    let cardHeight = 250;
    if (paymentType === -1) {
        cardHeight = 300;
        if (message) cardHeight += 50;
    }
    if (paymentType === PaymentType.CARD) cardHeight = 700;
    if (paymentType === PaymentType.ECHEQUE) cardHeight = 700;
    if (error) cardHeight = 250;

    if (!open) return null;
    return (
        <Modal onClose={onClose} open={open}>
            <Card className={classes.root} style={{height: cardHeight}}>
                {/*<LoadingOverlay loading={loadingData.loading || loading}>*/}
                    {!processing && (
                        <React.Fragment>
                            <CardHeader title={
                                'Make Payment: ' +
                                (paymentType === 0 ? PaymentTypes[PaymentType.CARD].name.charAt(0).toUpperCase() + PaymentTypes[PaymentType.CARD].name.substr(1) : '') +
                                (paymentType === 1 ? PaymentTypes[PaymentType.ECHEQUE].name.charAt(0).toUpperCase() + PaymentTypes[PaymentType.ECHEQUE].name.substr(1) : '')
                            }/>

                            <CardContent className={classes.container}>
                                {(paymentType === -1) && (
                                    <Grid container spacing={3}>

                                        {message && (
                                            <Grid item xs={12}>
                                                <Typography variant={'body1'}>{message}</Typography>
                                            </Grid>
                                        )}

                                        {/*<Grid item xs={12}>*/}
                                        {/*        <TextField*/}
                                        {/*            fullWidth*/}
                                        {/*            label="Billing Party Member"*/}
                                        {/*            name="billing_party_member_id"*/}
                                        {/*            onChange={(event) => setBillingPartyMemberId(Number(event.target.value))}*/}
                                        {/*            select*/}
                                        {/*            // eslint-disable-next-line react/jsx-sort-props*/}
                                        {/*            SelectProps={{native: true}}*/}
                                        {/*            value={billingPartyMemberId}*/}
                                        {/*            variant="outlined"*/}
                                        {/*            InputLabelProps={{*/}
                                        {/*                shrink: true,*/}
                                        {/*            }}*/}
                                        {/*            disabled={!!props.billingPartyMemberId}*/}
                                        {/*        >*/}
                                        {/*            /!*<option value={-1}>Please select...</option>*!/*/}
                                        {/*            /!*{payingMembers.map(member => (*!/*/}
                                        {/*            /!*    <option key={member.id} value={member.id}>*!/*/}
                                        {/*            /!*        {member.first_name} {member.last_name} ({member.email})*!/*/}
                                        {/*            /!*    </option>*!/*/}
                                        {/*            /!*))}*!/*/}
                                        {/*        </TextField>*/}
                                        {/*</Grid>*/}

                                    </Grid>
                                )}

                                <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Select Payment Frequency"
                                        value={paymentFrequency}
                                        onChange={(event) => {
                                            setPaymentFrequency(parseInt(event.target.value));
                                        }}
                                        variant="outlined"
                                        margin="dense"
                                        SelectProps={{
                                            native: false,
                                        }}
                                    >
                                        {/* <MenuItem value={-1}>Select Payment Frequency</MenuItem> */}
                                        <MenuItem value={0}>Pay according to Payment Schedule</MenuItem>
                                    </TextField>
                                </Grid>
                                </Grid>



                                {paymentType !== -1 && (
                                    <Grid container spacing={0} className={classes.summaryTable}>
                                        <Grid item xs={9}><Typography variant={'body1'}>Item</Typography></Grid>
                                        <Grid item xs={3}><Typography variant={'body1'}
                                                                      style={{textAlign: 'right'}}>Amount</Typography></Grid>

                                        <Grid item xs={9}><Typography
                                            variant={'body1'}>Payment Due</Typography></Grid>
                                        <Grid item xs={3}><Typography variant={'body1'}
                                                                      style={{textAlign: 'right'}}>{formatMoney(amount, true)}</Typography></Grid>

                                        {creditCardFee > 0 && (
                                            <React.Fragment>
                                                <Grid item xs={9} style={{display: 'inline'}}>
                                                    <Typography variant={'body1'}>
                                                        One time credit card fee.
                                                        <InfoPopover>
                                                            This is a one time card processing fee charge the first time
                                                            a card payment is taken.<br/>
                                                            It is charged
                                                            at {formatMoney(billingParty!.booking!.group!.credit_card_fee!)} per
                                                            traveller this billing party is responsible for.
                                                        </InfoPopover>
                                                    </Typography>

                                                </Grid>
                                                <Grid item xs={3}><Typography variant={'body1'}
                                                                              style={{textAlign: 'right'}}>{formatMoney(creditCardFee)}</Typography></Grid>
                                            </React.Fragment>
                                        )}

                                        <Grid item xs={9}><Typography variant={'body1'} style={{
                                            textAlign: 'right',
                                            fontWeight: 'bold'
                                        }}>Total</Typography></Grid>
                                        <Grid item xs={3}><Typography variant={'body1'} style={{
                                            textAlign: 'right',
                                            fontWeight: 'bold'
                                        }}>{formatMoney(nexioAmount, true)}</Typography></Grid>
                                    </Grid>
                                )}

                                {paymentType === 1 && (
                                    <Grid container spacing={0}>
                                        <Grid item xs={12}>
                                            <Typography variant={'body1'} color={'error'}>
                                                E-check payments take 5 - 7 business days to clear and show as “complete” on portal. Until “complete” status it will remain in past due status and not be included in the Received total. You can find the payment under “My Billing Parties” on the left side panel.
                                                Please refer to the question bubble to see a diagram that shows where to find your Routing Number and Account Number
                                                <InfoPopover>
                                                    <img alt="check image" className={classes.image} src={image}/>
                                                </InfoPopover>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}


                                {error && (
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant={'body1'} color={'error'}>{error}</Typography>
                                        </Grid>
                                    </Grid>
                                )}

                                {!error && paymentType !== -1 && (
                                    <iframe ref={frameRef} src={frameUrl} className={classes.iframe}/>
                                )}

                                <Grid container spacing={3} style={{marginTop: 15}}>
                                    <Grid item xs={12}>
                                        <Button
                                            onClick={() => {
                                                const newFee = billingParty && !billingParty.credit_card_fee_paid ? billingParty!.booking!.group!.credit_card_fee! * billingParty!.members!.length : 0;
                                                setPaymentType(PaymentType.CARD);
                                                setCreditCardFee(newFee);
                                                setNexioAmount(amount + newFee);
                                            }}
                                            variant="contained"
                                            // disabled={amount <= 0 || billingPartyMemberId === -1}
                                        >
                                            {PaymentTypes[PaymentType.CARD].name}
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setPaymentType(PaymentType.ECHEQUE);
                                                setNexioAmount(amount);
                                            }}
                                            variant="contained"
                                            // disabled={amount <= 0 || billingPartyMemberId === -1}
                                            style={{marginLeft: 10}}
                                        >
                                            {PaymentTypes[PaymentType.ECHEQUE].name}
                                        </Button>
                                        {/*{isFirstPayment && (*/}
                                        {/*    <Button onClick={onClose} variant="contained" style={{marginLeft: 10}}>*/}
                                        {/*        <T phrase="Paper Check"/>*/}
                                        {/*    </Button>*/}
                                        {/*)}*/}
                                    </Grid>
                                </Grid>



                            </CardContent>
                            <CardActions className={classes.actions}>
                                <Button onClick={onClose} variant="contained" className={classes.deleteButton}>
                                    <T phrase="Cancel"/>
                                </Button>
                                {paymentType !== -1 && !error && (
                                    <Button onClick={onPaymentSubmit} variant="contained"
                                            className={classes.submitButton}
                                            disabled={!isNexioValid || paymentFrequency=== -1}>
                                        <T phrase="Submit"/>
                                    </Button>
                                )}
                            </CardActions>
                        </React.Fragment>

                    )}
                {/*</LoadingOverlay>*/}
            </Card>
        </Modal>
    );
};

export default AutomaticPaymentCheckoutFrame;
